import { useCallback } from 'react';
import { useTracker, type TrackingEvent } from '@archipro-website/tracker';

import {
    trackTileMediaDetailClick,
    type TrackTileMediaItemType,
} from '~/modules/tracking/util/trackTileMedia';

/**
 * The event a tile click reports, keyed by the rail it was clicked in.
 *
 * A rail names itself with a `section`, and that name is the whole difference
 * between "someone clicked a tile" and "someone went from a project to one of
 * the products specified into it". The names are the ones the pre-revamp pages
 * used — a rail that changed its heading has not changed what it measures, and
 * renaming its event would split the series at the flag flip.
 *
 * `null` is a rail that is deliberately silent. A section with no entry reports
 * the generic `directoryTileClick` instead, which is what the browse grids want.
 *
 * Shared because two code paths report these: the Rocco tiles, which call
 * through the tile-actions atom, and {@link DetailTileCard}, which is not a
 * Rocco tile and logs for itself.
 */
export const TILE_CLICK_EVENT_BY_SECTION: Record<string, TrackingEvent | null> =
    {
        CategoryPageRecentlyViewed: 'recentlyViewedTileClick',
        ProductRange: 'ProductRangeClick',
        ProductFeaturedProjects: 'ProductProjectClick',
        // Carried over silent from the legacy page, which had no event for it.
        ProductRelated: null,
        ProductInstallers: 'ProductProfessionalInstallerClick',
        ProjectUsedProducts: 'ProductUsedProjectClick',
        ProfessionalProducts: 'ProfessionalProductClick',
        ProjectUsedProfessionals: 'ProfessionalUsedProjectClick',
        // The practice's other projects: the same clicks as the legacy
        // portfolio grid below, so both report one series.
        ProjectRelated: 'ViewProfileProjectClick',
        // "More in {category}": the legacy "Other projects you might like".
        ProjectSimilar: 'OtherProjectLikeProjectClick',
        ProfessionalProjects: 'ProfessionalProjectClick',
        ProfessionalArticles: 'ProfessionalArticleClick',
        ProfessionalNetwork: 'ProfessionalNetworkClick',
        ProfessionalBrandFamily: 'ProfessionalBrandFamilyClick',
        ProjectAboutProfessionalGrid: 'ViewProfileProjectClick',
    };

/**
 * Whether this rail reports an event of its own rather than the generic tile
 * click. A named rail that is deliberately silent still counts as named — it
 * must not fall back to `directoryTileClick`, which is not what the legacy page
 * reported for it.
 */
export const sectionNamesItsOwnEvent = (section?: string): boolean =>
    section !== undefined &&
    Object.hasOwn(TILE_CLICK_EVENT_BY_SECTION, section);

/** The rail's own event, or `undefined` for a rail that has none. */
export const tileClickEventForSection = (
    section?: string,
): TrackingEvent | null | undefined =>
    sectionNamesItsOwnEvent(section)
        ? TILE_CLICK_EVENT_BY_SECTION[section as string]
        : undefined;

/**
 * Reports a click on a rail that renders its own markup rather than a tile —
 * the sourcebook's index rows, the in-the-wild plates. They reach neither the
 * Rocco tiles' shared atom nor {@link DetailTileCard}, so without this a click
 * on one is recorded nowhere at all.
 *
 * Sends the same pair a tile sends: the rail's own event, and the site-wide
 * `TileMediaDetailClick` that the warehouse counts clicks with.
 */
export const useRailTileClickReport = (
    section: string,
    itemType: TrackTileMediaItemType,
) => {
    const tracker = useTracker();

    return useCallback(
        (item: { id: number; title: string; professionalId?: number }) => {
            const named = TILE_CLICK_EVENT_BY_SECTION[section];
            if (named) {
                void tracker.log(named, {
                    url: new URL(window.location.href),
                    data: {
                        LibraryItemID: item.id,
                        ProfessionalID: item.professionalId ?? 0,
                    },
                });
            }
            trackTileMediaDetailClick(tracker, {
                itemID: item.id,
                itemType,
                itemName: item.title,
            });
        },
        [tracker, section, itemType],
    );
};
